import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/features/auth/store'
import { StatusBadge } from '@/features/orders/StatusBadge'
import {
  ORDER_CHANNEL_LABELS,
  ORDER_STATUSES,
  STATUS_LABELS,
  exportOrders,
  formatMoney,
  listLocations,
  listOrders,
  type OrderChannel,
  type OrderFilters,
  type OrderStatus,
  type OrderSummary,
} from '@/features/orders/api'
import { listSales, type Sale } from '@/features/pos/api'

/**
 * A till sale is never a row in `sales_orders` -- see the module docstring on the API's
 * `pos` package for why -- so there is nothing to join server-side, and `sales`/`pos` are
 * forbidden from importing each other regardless (import-linter contracts in
 * `apps/api/pyproject.toml`). This screen instead fetches both lists and merges them here,
 * tagging each POS sale with the synthetic `pos` channel. A till sale is instant and fully
 * settled the moment it rings up, so it is shown already `completed` -- there is no draft,
 * approval or fulfilment step for a counter sale to be in.
 */
function saleToOrderSummary(sale: Sale): OrderSummary {
  return {
    id: sale.id,
    order_number: sale.receipt_number,
    customer_id: sale.customer_id ?? '',
    user_id: sale.cashier_id ?? '',
    location_id: sale.location_id,
    order_date: sale.completed_at,
    delivery_date: null,
    status: sale.status === 'voided' ? 'cancelled' : 'completed',
    channel: 'pos',
    subtotal: sale.subtotal,
    discount_amount: sale.discount_amount,
    tax_amount: sale.tax_amount,
    total: sale.total,
    shipping_amount: '0',
    amount_due: sale.total,
    approved_at: sale.completed_at,
    reservation_expires_at: null,
    created_at: sale.completed_at,
  }
}

// POS volume is a small fraction of sales-order volume (a till rings up one sale at a
// time; the order book carries thousands) -- one bounded fetch comfortably covers every
// completed till sale that could match a given filter, where the same cap on the *real*
// order list would silently drop rows (see below, real orders stay properly paginated).
const POS_PAGE_SIZE = 200

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const columns: Column<OrderSummary>[] = [
  {
    key: 'order_number',
    header: 'Order',
    className: 'font-medium',
    sortValue: (order) => order.order_number,
    // A POS row's `id` is a `pos_transactions` id, not a `sales_orders` one -- it has no
    // page at `/orders/:id` and never will, so it links to the receipt it actually is.
    cell: (order) => (
      <Link
        to={order.channel === 'pos' ? `/pos/sales/${order.id}` : `/orders/${order.id}`}
        className="hover:underline"
      >
        {order.order_number}
      </Link>
    ),
  },
  {
    key: 'order_date',
    header: 'Date',
    className: 'text-slate-500 dark:text-slate-400',
    sortValue: (order) => order.order_date,
    cell: (order) => new Date(order.order_date).toLocaleDateString(),
  },
  {
    key: 'status',
    header: 'Status',
    sortValue: (order) => order.status,
    cell: (order) => <StatusBadge status={order.status} />,
  },
  {
    key: 'channel',
    header: 'Source',
    className: 'text-slate-500 dark:text-slate-400',
    sortValue: (order) => order.channel,
    // Plain text rather than a second badge: the status badge is the thing to look at in
    // this row, and two coloured pills side by side stop either of them meaning anything.
    cell: (order) => ORDER_CHANNEL_LABELS[order.channel] ?? order.channel,
  },
  {
    key: 'delivery_date',
    header: 'Delivery',
    className: 'text-slate-500 dark:text-slate-400',
    sortValue: (order) => order.delivery_date,
    cell: (order) =>
      order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '—',
  },
  {
    key: 'total',
    header: 'Total',
    // Sorted on the number, rendered from the string: the API sends a NUMERIC as text
    // precisely so nothing rounds it, and sorting is the one place a number is safe.
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    // What the customer owes, delivery included -- the figure a storefront shopper paid.
    sortValue: (order) => Number(order.amount_due),
    cell: (order) => formatMoney(order.amount_due),
  },
]

export function Orders() {
  const can = useAuth((s) => s.can)
  // The courier queue is kept in the URL, so it survives a reload and can be linked to --
  // by those shown the filter only, or a rep would get a list cut by a box they cannot see.
  const [params, setParams] = useSearchParams()
  const [filters, setFilters] = useState<OrderFilters>(() => ({
    page: 1,
    ...(params.get('needs_courier') === 'true' &&
      can('invoices', 'update') && { needs_courier: true }),
  }))
  useEffect(() => {
    setParams(
      (current) => {
        if (filters.needs_courier) current.set('needs_courier', 'true')
        else current.delete('needs_courier')
        return current
      },
      { replace: true },
    )
  }, [filters.needs_courier, setParams])
  const navigate = useNavigate()

  // 'pos' is not a real value of the `channel` column and the API rejects it as a filter
  // (a CHECK constraint backs `internal`/`marketplace`), so it never reaches `listOrders`.
  // Picking it means "till sales only" and switches the other query off; leaving the
  // source filter on "any" runs both and merges them.
  const showOrders = filters.channel !== 'pos'
  // A till sale never ships by courier, so the courier filter leaves them out too.
  const showPos =
    filters.channel !== 'internal' && filters.channel !== 'marketplace' && !filters.needs_courier
  const page = filters.page ?? 1

  const { data, isLoading, error } = useQuery({
    queryKey: ['orders', filters],
    queryFn: () => listOrders(filters),
    enabled: showOrders,
    retry: false,
  })

  const {
    data: salesData,
    isLoading: isLoadingSales,
    error: salesError,
  } = useQuery({
    queryKey: [
      'orders-pos-sales',
      filters.location_id,
      filters.date_from,
      filters.date_to,
      showPos,
    ],
    queryFn: () =>
      listSales({
        location_id: filters.location_id,
        date_from: filters.date_from,
        date_to: filters.date_to,
        status: 'completed',
        page_size: POS_PAGE_SIZE,
      }),
    enabled: showPos,
    retry: false,
  })

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
    retry: false,
  })
  const locations = locationsPage?.data ?? []

  const posOrders = useMemo(
    () => (showPos ? (salesData?.data ?? []).map(saleToOrderSummary) : []),
    [salesData, showPos],
  )

  // The order book runs to thousands of rows -- keeping it on the server's own pagination
  // is the only way "page 12 of 340" stays true. Till sales run to a handful, so instead of
  // fighting for a slot in that same page, every one of them is shown on page 1 alongside
  // the newest real orders: that is where a cashier checking "did my sale show up?" and an
  // office worker opening the screen fresh both already look. Paging past 1 shows real
  // orders only -- exactly the table's original behaviour before POS sales joined it.
  const orders = useMemo(() => {
    // Source filter set to "POS": nothing to page server-side about a bounded, sub-200-row
    // fetch -- hand every matching till sale to the table and let its own client pager
    // slice it, same as any other small, whole-set screen.
    if (!showOrders) return posOrders
    const real = data?.data ?? []
    if (page !== 1) return real
    return [...real, ...posOrders].sort(
      (a, b) => new Date(b.order_date).getTime() - new Date(a.order_date).getTime(),
    )
  }, [data, posOrders, showOrders, page])

  const ordersLoading = (showOrders && isLoading) || (showPos && isLoadingSales)
  const meta = data?.meta
  // The header counts every matching row, not just what is on screen -- otherwise a filter
  // that matches 2,791 orders would report "25" the moment pagination trims the page down.
  const totalCount = (meta?.total ?? 0) + (showPos ? (salesData?.meta.total ?? 0) : 0)

  /** Any filter change resets to page 1: page 4 of a narrower result set is usually empty. */
  const set = (patch: Partial<OrderFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Sales orders</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {ordersLoading ? 'Loading…' : `${totalCount} order${totalCount === 1 ? '' : 's'}`}
          </p>
        </div>
        {can('invoices', 'create') && (
          <Button onClick={() => navigate('/orders/new')}>New order</Button>
        )}
      </header>

      <Card>
        <CardHeader
          title="All orders"
          description="Orders raised in the business plus completed till sales, newest first."
        />
        <DataTable
          rows={orders}
          columns={columns}
          getRowId={(order) => order.id}
          caption="Sales orders"
          isLoading={ordersLoading}
          error={error ?? salesError}
          emptyMessage="No orders match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="order-status">
                Status
              </label>
              <select
                id="order-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) =>
                  set({
                    status: event.target.value as OrderStatus | '',
                    // The two controls ask the same question; letting both apply at once
                    // would show an empty table and look like a bug.
                    awaiting_approval: false,
                  })
                }
              >
                <option value="">Any status</option>
                {ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="order-channel">
                Source
              </label>
              <select
                id="order-channel"
                className={SELECT_CLASS}
                value={filters.channel ?? ''}
                onChange={(event) => set({ channel: event.target.value as OrderChannel | '' })}
              >
                <option value="">Any source</option>
                {(Object.keys(ORDER_CHANNEL_LABELS) as OrderChannel[]).map((channel) => (
                  <option key={channel} value={channel}>
                    {ORDER_CHANNEL_LABELS[channel]}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="order-location">
                Location
              </label>
              <select
                id="order-location"
                className={SELECT_CLASS}
                value={filters.location_id ?? ''}
                onChange={(event) => set({ location_id: event.target.value })}
              >
                <option value="">Any location</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="order-from">
                From
              </label>
              <Input
                id="order-from"
                type="date"
                className="w-auto"
                value={filters.date_from ?? ''}
                onChange={(event) => set({ date_from: event.target.value })}
              />
              <span className="text-sm text-slate-400">to</span>
              <label className="sr-only" htmlFor="order-to">
                To
              </label>
              <Input
                id="order-to"
                type="date"
                className="w-auto"
                value={filters.date_to ?? ''}
                onChange={(event) => set({ date_to: event.target.value })}
              />

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={Boolean(filters.awaiting_approval)}
                  onChange={(event) => set({ awaiting_approval: event.target.checked, status: '' })}
                />
                Awaiting approval
              </label>

              {/* Only those who may book the courier (`book-shipment` needs invoices:update). */}
              {can('invoices', 'update') && (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={Boolean(filters.needs_courier)}
                    onChange={(event) => set({ needs_courier: event.target.checked })}
                  />
                  Needs courier
                </label>
              )}

              {(filters.status ||
                filters.channel ||
                filters.location_id ||
                filters.date_from ||
                filters.date_to ||
                filters.awaiting_approval ||
                filters.needs_courier) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ page: 1 })}>
                  Clear
                </Button>
              )}

              <ExportButton
                filename="sales_orders"
                selectClassName={SELECT_CLASS}
                // 'pos' is not a real channel the export endpoint understands (it filters
                // the same `sales_orders` table this screen otherwise merges POS sales
                // into) -- till receipts have their own PDF/email export on the POS screen.
                onExport={(format) =>
                  exportOrders({ ...filters, channel: showOrders ? filters.channel : '' }, format)
                }
                errorMessage="Could not export the orders."
              />
            </div>
          }
          search={{
            placeholder: 'Search order number…',
            matches: (order, query) => order.order_number.toLowerCase().includes(query),
          }}
          // The real order book is paginated server-side, same as before POS sales joined
          // this screen -- the day's till sales just ride along on page 1 (see `orders`
          // above). Filtered down to POS only, there is no server page to drive: the whole
          // matching set was already fetched, so the table pages it client-side instead.
          pagination={
            showOrders
              ? {
                  page,
                  totalPages: meta?.total_pages ?? 1,
                  onPage: (newPage) => setFilters((current) => ({ ...current, page: newPage })),
                  isFetching: isLoading,
                  total: meta?.total,
                }
              : undefined
          }
          pageSize={showOrders ? undefined : 25}
        />
      </Card>
    </div>
  )
}
